import React, {FC} from 'react';

import {defineMessage, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {getAnalyticsSelectedNamespace} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {helpCenterUrl} from '../../../../store/stateless/navigationActions';
import {
  BasicReportProps,
  ContributingSegmentsOptionsEnum,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {AnalyticsComputedProgressMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';
import {EmptyStateRenderer} from '../../reportElements/charts/emptyState';
import {ReportProgressBar} from '../../reportElements/charts/reportProgressBar';
import {SetupEmptyState} from '../../reportElements/charts/setupEmptyState';
import {ReportTable} from '../../reportElements/table/reportTable';

/*
 * Constants.
 */

const defaultColumnIds = ['num_messages_sent', 'num_replies_sent', 'avg_handle_time', 'customer_reply_rate'];

enum MessageTemplateBarSubmetricsEnum {
  WITH_MESSAGE_TEMPLATE = 'withMessageTemplate',
  WITHOUT_MESSAGE_TEMPLATE = 'withoutMessageTemplate',
}

const msgTemplateTableInitialState = {
  sortBy: [
    {
      id: 'num_messages_sent',
      desc: true,
    },
  ],
};

/*
 * Props.
 */

interface MessageTemplateReportProps extends BasicReportProps {
  isTeamColumnVisible: boolean;
}

/*
 * Component.
 */

const progressBarLegendRenderers = [
  (val: number) => <Trans id="message-template-pct-with-legend-label">{val}% with</Trans>,
  (val: number) => <Trans id="message-template-pct-without-legend-label">{val}% without</Trans>,
];

export const MessageTemplateReport: FC<MessageTemplateReportProps> = ({isLoading}) => {
  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const namespace = useAppSelector(getAnalyticsSelectedNamespace);

  const onSerieSelect = (serieIndex: number) => {
    const isMessageTemplateSerie = serieIndex === 0;
    dispatch(
      setMetricDetailsAction(
        AnalyticsScalarMetricsEnum.PCT_MESSAGES_WITH_MESSAGE_TEMPLATES,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: isMessageTemplateSerie
              ? MessageTemplateBarSubmetricsEnum.WITH_MESSAGE_TEMPLATE
              : MessageTemplateBarSubmetricsEnum.WITHOUT_MESSAGE_TEMPLATE,
          },
        },
        isMessageTemplateSerie
          ? localize(
              defineMessage({
                id: 'metric-details-with-message-templates',
                message: 'With message templates',
              }),
            )
          : localize(
              defineMessage({
                id: 'metric-details-without-message-templates',
                message: 'Without message templates',
              }),
            ),
      ),
    );
  };

  return (
    <>
      {/* Messages with with message templates */}
      <ReportProgressBar
        reportSectionId={ReportElementIdsEnum.MESSAGE_TEMPLATES_PCT}
        isLoading={isLoading}
        metric={AnalyticsComputedProgressMetricsEnum.MESSAGE_TEMPLATE_REPORT_PROGRESS}
        onSerieSelect={onSerieSelect}
        legendRenderers={progressBarLegendRenderers}
      />

      {/* Message Template Table */}
      <ReportTable
        reportSectionId={ReportElementIdsEnum.MESSAGE_TEMPLATE_TABLE}
        isLoading={isLoading}
        metric={AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE}
        defaultColumnIds={defaultColumnIds}
        renderEmptyState={renderEmptyState(namespace)}
        disableSelection
        initialState={msgTemplateTableInitialState}
      />
    </>
  );
};

/*
 * Helpers.
 */

function renderEmptyState(namespace: string) {
  const setupLink = `/settings/${namespace}/answers`;

  const messageTemplatesEmptyStateRenderer: EmptyStateRenderer = ({width, height}) => (
    <SetupEmptyState
      width={width}
      height={height}
      setupLink={setupLink}
      title={
        <Trans id="analytics-message-template-table-empty-state-no-message-templates-table-title">
          Message templates not set up
        </Trans>
      }
      buttonText={
        <Trans id="analytics-message-template-table-empty-state-no-message-template-button">
          Set up Message Templates
        </Trans>
      }
      learnMoreLink={`${helpCenterUrl}/t/63244r`}
      interactionId="analytics-message-template-table-empty-set-up-rule"
    />
  );

  return messageTemplatesEmptyStateRenderer;
}
